import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Users, Clock, Shield, Plus, Trash2, CheckCircle, XCircle, ChevronDown, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const ROLE_COLORS = {
  admin:  'bg-blue-100 text-blue-700 border border-blue-200',
  editor: 'bg-amber-100 text-amber-700 border border-amber-200',
  caster: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
};

const ROLE_DESCRIPTIONS = {
  caster: 'Create & submit stories',
  editor: 'Review & approve stories',
  admin:  'Full access',
};

const STATUS_COLORS = {
  approved: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
  pending:  'bg-amber-100 text-amber-700 border border-amber-200',
  rejected: 'bg-red-100 text-red-700 border border-red-200',
};

const ROLES = ['caster', 'editor', 'admin'];
const USERS_PAGE_SIZE = 10;

function Avatar({ name, picture, size = 8 }) {
  if (picture) {
    return <img src={picture} alt={name} className={`w-${size} h-${size} rounded-full object-cover shrink-0`} />;
  }
  return (
    <div className={`w-${size} h-${size} rounded-full bg-[#F8EBD9] border border-[#E7D2B8] flex items-center justify-center shrink-0`}>
      <span className="text-[#6E6258] text-sm font-medium">{(name || '?')[0].toUpperCase()}</span>
    </div>
  );
}

function RoleDropdown({ currentRole, userId, onRoleChange }) {
  const [open, setOpen]       = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = async (role) => {
    if (role === currentRole) { setOpen(false); return; }
    setLoading(true);
    try {
      await api.put(`/users/${userId}/role`, { role });
      onRoleChange(userId, role);
      toast.success('Role updated');
    } catch {
      toast.error('Failed to update role');
    } finally {
      setLoading(false);
      setOpen(false);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        disabled={loading}
        className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded capitalize ${ROLE_COLORS[currentRole]} cursor-pointer hover:opacity-80 transition-opacity`}
      >
        {currentRole}
        <ChevronDown size={12} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-20 bg-card border border-border rounded-lg shadow-lg py-1 min-w-[100px]">
            {ROLES.map(r => (
              <button
                key={r}
                onClick={() => handleChange(r)}
                className={`w-full text-left px-3 py-1.5 text-xs capitalize hover:bg-[#FFF4E8] transition-colors ${
                  r === currentRole ? 'text-foreground font-medium' : 'text-muted-foreground'
                }`}
              >
                {r}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Pagination component ───
function Pagination({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;
  const pages = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
    .reduce((acc, p, idx, arr) => {
      if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
      acc.push(p);
      return acc;
    }, []);

  return (
    <div className="flex items-center justify-between pt-4 mt-2 border-t border-border">
      <Button
        variant="outline" size="sm"
        onClick={() => onChange(page - 1)}
        disabled={page <= 1}
        className="border-border text-foreground hover:bg-[#FFF4E8] gap-1.5 disabled:opacity-40"
      >
        <ChevronLeft size={16} /> Previous
      </Button>
      <div className="flex items-center gap-1">
        {pages.map((p, idx) =>
          p === '...' ? (
            <span key={`e-${idx}`} className="px-2 text-[#9C8E84] text-sm">…</span>
          ) : (
            <button
              key={p}
              onClick={() => onChange(p)}
              className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                p === page ? 'bg-[#1F1A17] text-[#FFE4C4]' : 'text-foreground hover:bg-[#FFF4E8]'
              }`}
            >
              {p}
            </button>
          )
        )}
      </div>
      <Button
        variant="outline" size="sm"
        onClick={() => onChange(page + 1)}
        disabled={page >= totalPages}
        className="border-border text-foreground hover:bg-[#FFF4E8] gap-1.5 disabled:opacity-40"
      >
        Next <ChevronRight size={16} />
      </Button>
    </div>
  );
}


// ─── Tab: Active Users ───
function ActiveUsersTab({ currentUserId }) {
  const [users, setUsers]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [roleFilter, setRoleFilter]     = useState('all');
  const [statusFilter, setStatusFilter] = useState('approved'); // default: approved users
  const [sortBy, setSortBy]       = useState('join_date_desc');
  const [page, setPage]           = useState(1);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch all users (backend now accepts optional access_status param)
      const res = await api.get('/users');
      setUsers(res.data);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleRoleChange = (userId, newRole) => {
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
  };

  const handleRevoke = async (userId, name) => {
    if (!window.confirm(`Revoke access for ${name}? They will need re-approval to log in.`)) return;
    try {
      await api.put(`/users/${userId}/revoke`);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, access_status: 'pending' } : u));
      toast.success('Access revoked');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to revoke access');
    }
  };

  const filtered = useMemo(() => {
    let result = [...users];
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter(u =>
        u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q)
      );
    }
    if (roleFilter !== 'all')   result = result.filter(u => u.role === roleFilter);
    if (statusFilter !== 'all') result = result.filter(u => u.access_status === statusFilter);

    result.sort((a, b) => {
      if (sortBy === 'join_date')      return new Date(a.created_at) - new Date(b.created_at);
      if (sortBy === 'join_date_desc') return new Date(b.created_at) - new Date(a.created_at);
      if (sortBy === 'last_active') {
        return new Date(b.requested_at || b.created_at) - new Date(a.requested_at || a.created_at);
      }
      if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
      return 0;
    });
    return result;
  }, [users, search, roleFilter, statusFilter, sortBy]);

  // Reset to page 1 whenever filters change
  useEffect(() => { setPage(1); }, [search, roleFilter, statusFilter, sortBy]);

  const totalPages  = Math.max(1, Math.ceil(filtered.length / USERS_PAGE_SIZE));
  const paginated   = filtered.slice((page - 1) * USERS_PAGE_SIZE, page * USERS_PAGE_SIZE);

  if (loading) return <div className="text-muted-foreground text-sm py-8 text-center">Loading...</div>;

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9C8E84]" size={15} />
          <Input
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search by name or email..."
            className="pl-9 bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84] h-9 text-sm"
          />
        </div>

        <Select value={roleFilter} onValueChange={v => { setRoleFilter(v); setPage(1); }}>
          <SelectTrigger className="w-32 bg-white border-border text-foreground h-9 text-sm">
            <SelectValue placeholder="All Roles" />
          </SelectTrigger>
          <SelectContent className="bg-white border-border">
            <SelectItem value="all"    className="text-[#1F1A17] focus:bg-[#FFF4E8]">All Roles</SelectItem>
            {ROLES.map(r => (
              <SelectItem key={r} value={r} className="text-[#1F1A17] focus:bg-[#FFF4E8] capitalize">{r}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-36 bg-white border-border text-foreground h-9 text-sm">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent className="bg-white border-border">
            <SelectItem value="all"      className="text-[#1F1A17] focus:bg-[#FFF4E8]">All Status</SelectItem>
            <SelectItem value="approved" className="text-[#1F1A17] focus:bg-[#FFF4E8]">Approved</SelectItem>
            <SelectItem value="pending"  className="text-[#1F1A17] focus:bg-[#FFF4E8]">Pending</SelectItem>
            <SelectItem value="rejected" className="text-[#1F1A17] focus:bg-[#FFF4E8]">Rejected</SelectItem>
          </SelectContent>
        </Select>

        <Select value={sortBy} onValueChange={v => { setSortBy(v); setPage(1); }}>
          <SelectTrigger className="w-52 bg-white border-border text-foreground h-9 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white border-border">
            <SelectItem value="join_date_desc" className="text-[#1F1A17] focus:bg-[#FFF4E8]">Joined — Newest first</SelectItem>
            <SelectItem value="join_date"      className="text-[#1F1A17] focus:bg-[#FFF4E8]">Joined — Oldest first</SelectItem>
            <SelectItem value="last_active"    className="text-[#1F1A17] focus:bg-[#FFF4E8]">Last Active</SelectItem>
            <SelectItem value="name"           className="text-[#1F1A17] focus:bg-[#FFF4E8]">Name A–Z</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <p className="text-xs text-[#9C8E84]">
        {filtered.length} user{filtered.length !== 1 ? 's' : ''}
        {filtered.length !== users.length && ` of ${users.length}`}
        {filtered.length > USERS_PAGE_SIZE && ` — page ${page} of ${totalPages}`}
      </p>

      {paginated.length === 0 ? (
        <div className="text-muted-foreground text-sm text-center py-8">No users match your filters.</div>
      ) : (
        <>
          <div className="space-y-2">
            {paginated.map(u => (
              <div
                key={u.id}
                className="flex items-center gap-4 bg-card rounded-xl px-4 py-3 border border-border hover:border-[#C9B49A] transition-colors"
              >
                <Avatar name={u.name} picture={u.picture} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{u.name || '—'}</p>
                  <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className={`text-xs px-2 py-0.5 rounded border capitalize hidden sm:block ${STATUS_COLORS[u.access_status] || ''}`}>
                    {u.access_status}
                  </span>
                  <span className="text-xs text-[#9C8E84] hidden md:block">
                    Joined {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                  </span>
                  {/* Only show role dropdown for approved users */}
                  {u.access_status === 'approved' && (
                    <RoleDropdown currentRole={u.role} userId={u.id} onRoleChange={handleRoleChange} />
                  )}
                  {u.id !== currentUserId && u.access_status === 'approved' && (
                    <button
                      onClick={() => handleRevoke(u.id, u.name || u.email)}
                      className="text-[#C9B49A] hover:text-red-500 transition-colors p-1"
                      title="Revoke access"
                    >
                      <XCircle size={16} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </>
      )}

      {/* Role legend — moved from Editorial's Team Management tab */}
      <div className="bg-card rounded-xl px-4 py-4 border border-border">
        <p className="text-xs text-muted-foreground mb-3 font-medium">Role permissions</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {ROLES.map(r => (
            <div key={r} className="space-y-1">
              <span className={`inline-block text-xs px-2 py-0.5 rounded capitalize ${ROLE_COLORS[r]}`}>{r}</span>
              <p className="text-xs text-muted-foreground">{ROLE_DESCRIPTIONS[r]}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


// ─── Tab: Pending Requests ───
function PendingTab({ onCountChange }) {
  const [pending, setPending]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [approving, setApproving]       = useState({});
  const [roleSelections, setRoleSelections] = useState({});
  const [search, setSearch]             = useState('');
  const [page, setPage]                 = useState(1);

  const fetchPending = useCallback(async () => {
    try {
      const res = await api.get('/users/pending');
      setPending(res.data);
      onCountChange(res.data.length);
    } catch {
      toast.error('Failed to load pending requests');
    } finally {
      setLoading(false);
    }
  }, [onCountChange]);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  const handleApprove = async (userId, name) => {
    const role = roleSelections[userId] || 'caster';
    setApproving(prev => ({ ...prev, [userId]: 'approving' }));
    try {
      await api.put(`/users/${userId}/approve`, { role });
      const updated = pending.filter(u => u.id !== userId);
      setPending(updated);
      onCountChange(updated.length);
      toast.success(`${name || 'User'} approved as ${role}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve user');
    } finally {
      setApproving(prev => ({ ...prev, [userId]: null }));
    }
  };

  const handleReject = async (userId, name) => {
    setApproving(prev => ({ ...prev, [userId]: 'rejecting' }));
    try {
      await api.put(`/users/${userId}/reject`);
      const updated = pending.filter(u => u.id !== userId);
      setPending(updated);
      onCountChange(updated.length);
      toast.success(`${name || 'User'} rejected`);
    } catch {
      toast.error('Failed to reject user');
    } finally {
      setApproving(prev => ({ ...prev, [userId]: null }));
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return pending;
    const q = search.trim().toLowerCase();
    return pending.filter(u =>
      u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q)
    );
  }, [pending, search]);

  useEffect(() => { setPage(1); }, [search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / USERS_PAGE_SIZE));
  const paginated  = filtered.slice((page - 1) * USERS_PAGE_SIZE, page * USERS_PAGE_SIZE);

  if (loading) return <div className="text-muted-foreground text-sm py-8 text-center">Loading...</div>;

  if (pending.length === 0) {
    return (
      <div className="text-center py-12">
        <CheckCircle size={40} className="text-emerald-600 mx-auto mb-3" />
        <p className="text-foreground font-medium">All clear</p>
        <p className="text-muted-foreground text-sm mt-1">No pending access requests</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9C8E84]" size={15} />
        <Input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name or email..."
          className="pl-9 bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84] h-9 text-sm"
        />
      </div>

      <p className="text-xs text-[#9C8E84]">
        {filtered.length} request{filtered.length !== 1 ? 's' : ''}
        {filtered.length !== pending.length && ` of ${pending.length}`}
      </p>

      {paginated.length === 0 ? (
        <p className="text-muted-foreground text-sm text-center py-4">No results match your search.</p>
      ) : (
        <>
          <div className="space-y-3">
            {paginated.map(u => (
              <div key={u.id} className="bg-card rounded-xl px-4 py-4 border border-amber-200">
                <div className="flex items-start gap-4">
                  <Avatar name={u.name} picture={u.picture} size={10} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">{u.name || '—'}</p>
                    <p className="text-xs text-muted-foreground">{u.email}</p>
                    <p className="text-xs text-[#9C8E84] mt-1">
                      Requested {u.requested_at ? new Date(u.requested_at).toLocaleString() : 'recently'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-4 pt-3 border-t border-border">
                  <span className="text-xs text-muted-foreground shrink-0">Assign role:</span>
                  <select
                    value={roleSelections[u.id] || 'caster'}
                    onChange={e => setRoleSelections(prev => ({ ...prev, [u.id]: e.target.value }))}
                    className="bg-white border border-border text-[#1F1A17] text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-[#1F1A17]"
                  >
                    {ROLES.map(r => <option key={r} value={r} className="capitalize">{r}</option>)}
                  </select>
                  <div className="flex gap-2 ml-auto">
                    <Button
                      size="sm"
                      onClick={() => handleReject(u.id, u.name)}
                      disabled={!!approving[u.id]}
                      className="bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 text-xs h-8"
                    >
                      <XCircle size={14} className="mr-1" />
                      {approving[u.id] === 'rejecting' ? 'Rejecting...' : 'Reject'}
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleApprove(u.id, u.name)}
                      disabled={!!approving[u.id]}
                      className="bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 text-xs h-8"
                    >
                      <CheckCircle size={14} className="mr-1" />
                      {approving[u.id] === 'approving' ? 'Approving...' : 'Approve'}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </>
      )}
    </div>
  );
}


// ─── Tab: Allowlist ───
function AllowlistTab() {
  const [entries, setEntries]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [adding, setAdding]     = useState(false);
  const [search, setSearch]     = useState('');
  const [page, setPage]         = useState(1);

  const fetchEntries = useCallback(async () => {
    try {
      const res = await api.get('/approved-emails');
      setEntries(res.data);
    } catch {
      toast.error('Failed to load allowlist');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const handleAdd = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) { toast.error('Enter a valid email address'); return; }
    setAdding(true);
    try {
      const res = await api.post('/approved-emails', { email });
      setEntries(prev => [res.data, ...prev]);
      setNewEmail('');
      toast.success(`${email} added to allowlist`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add email');
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (id, email) => {
    if (!window.confirm(`Remove ${email} from allowlist? They will lose access.`)) return;
    try {
      await api.delete(`/approved-emails/${id}`);
      setEntries(prev => prev.filter(e => e.id !== id));
      toast.success('Email removed from allowlist');
    } catch {
      toast.error('Failed to remove email');
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return entries;
    const q = search.trim().toLowerCase();
    return entries.filter(e =>
      e.email?.toLowerCase().includes(q) || e.added_by_name?.toLowerCase().includes(q)
    );
  }, [entries, search]);

  useEffect(() => { setPage(1); }, [search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / USERS_PAGE_SIZE));
  const paginated  = filtered.slice((page - 1) * USERS_PAGE_SIZE, page * USERS_PAGE_SIZE);

  if (loading) return <div className="text-muted-foreground text-sm py-8 text-center">Loading...</div>;

  return (
    <div className="space-y-4">
      {/* Add email */}
      <div className="bg-card rounded-xl p-4 border border-border">
        <p className="text-sm text-foreground mb-1 font-medium">Pre-approve an email</p>
        <p className="text-xs text-muted-foreground mb-3">
          Users on this list get instant access when they sign in with Google — no approval required.
        </p>
        <div className="flex gap-2">
          <input
            type="email"
            value={newEmail}
            onChange={e => setNewEmail(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
            placeholder="name@example.com"
            className="flex-1 bg-white border border-border text-[#1F1A17] text-sm rounded-lg px-3 py-2 placeholder-[#9C8E84] focus:outline-none focus:border-[#1F1A17]"
          />
          <Button
            onClick={handleAdd}
            disabled={adding || !newEmail.trim()}
            className="bg-[#1F1A17] hover:bg-[#2B241F] text-[#FFE4C4] text-sm px-4 shrink-0"
          >
            <Plus size={16} className="mr-1" />
            {adding ? 'Adding...' : 'Add'}
          </Button>
        </div>
      </div>

      {entries.length > 0 && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9C8E84]" size={15} />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search allowlist..."
            className="pl-9 bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84] h-9 text-sm"
          />
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground text-sm">
          {entries.length === 0 ? 'No pre-approved emails yet.' : 'No results match your search.'}
        </div>
      ) : (
        <>
          <p className="text-xs text-[#9C8E84]">
            {filtered.length} entr{filtered.length !== 1 ? 'ies' : 'y'}
            {filtered.length !== entries.length && ` of ${entries.length}`}
          </p>

          <div className="space-y-2">
            {paginated.map(e => (
              <div
                key={e.id}
                className="flex items-center gap-3 bg-card rounded-xl px-4 py-3 border border-border hover:border-[#C9B49A] transition-colors"
              >
                <Shield size={16} className="text-blue-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground truncate">{e.email}</p>
                  <p className="text-xs text-muted-foreground">
                    Added {e.created_at ? new Date(e.created_at).toLocaleDateString() : '—'}
                    {e.added_by_name ? ` by ${e.added_by_name}` : ''}
                  </p>
                </div>
                <button
                  onClick={() => handleRemove(e.id, e.email)}
                  className="text-[#C9B49A] hover:text-red-500 transition-colors p-1 shrink-0"
                  title="Remove from allowlist"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>

          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </>
      )}
    </div>
  );
}


// ─── Main Page ───
export default function UsersPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab]     = useState('active');
  const [pendingCount, setPendingCount] = useState(0);

  if (user?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  const tabs = [
    { id: 'active',    label: 'Active Users',    icon: Users },
    { id: 'pending',   label: 'Pending Requests', icon: Clock,  badge: pendingCount },
    { id: 'allowlist', label: 'Allowlist',         icon: Shield },
  ];

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-foreground font-outfit">User Management</h1>
        <p className="text-muted-foreground text-sm mt-1">Manage who can access Tempust</p>
      </div>

      <div className="flex gap-1 bg-[#FFF4E8] p-1 rounded-xl border border-border mb-6 w-fit">
        {tabs.map(({ id, label, icon: Icon, badge }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === id
                ? 'bg-[#FFE4C4] text-[#1F1A17] border border-[#E7D2B8]'
                : 'text-muted-foreground hover:text-foreground hover:bg-[#FFE4C4]/50'
            }`}
          >
            <Icon size={15} />
            {label}
            {badge > 0 && (
              <span className="bg-amber-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {activeTab === 'active'    && <ActiveUsersTab currentUserId={user?.user_id} />}
      {activeTab === 'pending'   && <PendingTab onCountChange={setPendingCount} />}
      {activeTab === 'allowlist' && <AllowlistTab />}
    </div>
  );
}